import { useEffect, useState } from "react";
import { Upload, Camera, X, ImageIcon } from "lucide-react";
import { cardImageUrl } from "@/lib/api";

// Convertir HEIC/HEIF (iPhone) → JPEG antes de meter el archivo al state.
// Sin esto, `URL.createObjectURL(f)` sobre HEIC devuelve un blob que ningún
// navegador puede renderizar → preview negra "IMG_XXXX.HEIC" en Mac/Chrome.
// Al convertir aquí, el mismo File JPG se usa para preview + upload en un
// solo paso — sin subir HEIC al backend que a veces peleaba con formato.
async function normalizeHeic(file) {
  if (!file) return file;
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  const isHeic = name.endsWith(".heic") || name.endsWith(".heif") || type === "image/heic" || type === "image/heif";
  if (!isHeic) return file;
  try {
    const mod = await import("heic-to");
    const converter = mod.heicTo || mod.default || mod;
    const jpegBlob = await converter({ blob: file, type: "image/jpeg", quality: 0.85 });
    return new File([jpegBlob], name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
  } catch (e) {
    console.error("HEIC conversion failed for", name, e);
    // Devolvemos el original; el backend intentará convertir con pillow_heif
    // como red de seguridad. Al menos no bloqueamos la subida.
    return file;
  }
}

/** Reusable multi-photo input.
 * - APPENDS newly picked files to the existing list (never replaces).
 * - Dedupes by name+size so repeated picks don't create duplicates.
 * - Separate "Take photo" button on mobile (uses `capture="environment"`)
 *   so each capture adds ONE more photo instead of replacing the list.
 * - Renders per-photo thumbnails with an individual remove (X) button.
 * - Enforces a `max` cap.
 * - Auto-convierte HEIC/HEIF (iPhone) a JPEG antes de mostrar preview.
 */
export function MultiPhotoInput({
  photos,
  onChange,
  min = 0,
  recommended = 0,
  max = 20,
  testid = "multi-photo",
  hint = "",
  fullPreview = false,
}) {
  const [previews, setPreviews] = useState([]);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Fotos ya subidas (borradores) llegan como { _draft_path } en vez de File.
    const urls = photos.map((f) => (f instanceof Blob ? URL.createObjectURL(f) : cardImageUrl(f?._draft_path)));
    if (!cancelled) setPreviews(urls);
    return () => {
      cancelled = true;
      urls.forEach((u) => u && u.startsWith("blob:") && URL.revokeObjectURL(u));
    };
  }, [photos]);

  const addFiles = async (fileList) => {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;
    setConverting(true);
    try {
      // Convertir HEIC en paralelo (rápido en Mac moderno; ~200-500ms/foto)
      const normalized = await Promise.all(incoming.map(normalizeHeic));
      const key = (f) => `${f.name}::${f.size}`;
      const existing = new Set(photos.map(key));
      const merged = [...photos];
      for (const f of normalized) {
        if (existing.has(key(f))) continue;
        if (merged.length >= max) break;
        merged.push(f);
        existing.add(key(f));
      }
      onChange(merged);
    } finally {
      setConverting(false);
    }
  };

  const removeAt = (i) => {
    const next = photos.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const remaining = Math.max(0, max - photos.length);
  const short = photos.length < min ? min - photos.length : 0;
  const shortRec = recommended > min && photos.length >= min && photos.length < recommended
    ? recommended - photos.length
    : 0;

  return (
    <div data-testid={`${testid}-container`}>
      {/* Actions: gallery pick + camera capture (separate inputs so each one appends) */}
      <div className="grid grid-cols-2 gap-2">
        <label
          className={`flex items-center justify-center gap-2 p-3 bg-[#0A0A0A] border border-dashed rounded-md cursor-pointer transition-colors ${remaining === 0 ? "border-[#262626] opacity-50 cursor-not-allowed" : "border-[#262626] hover:border-[#EF4444]"}`}
          data-testid={`${testid}-pick`}
        >
          <Upload size={16} className="text-neutral-400" />
          <span className="text-xs text-neutral-300">Elegir fotos</span>
          <input
            data-testid={testid}
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            disabled={remaining === 0}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            className="hidden"
          />
        </label>
        <label
          className={`flex items-center justify-center gap-2 p-3 bg-[#0A0A0A] border border-dashed rounded-md cursor-pointer transition-colors ${remaining === 0 ? "border-[#262626] opacity-50 cursor-not-allowed" : "border-[#262626] hover:border-[#EF4444]"}`}
          data-testid={`${testid}-camera-label`}
        >
          <Camera size={16} className="text-neutral-400" />
          <span className="text-xs text-neutral-300">Tomar foto</span>
          <input
            data-testid={`${testid}-camera`}
            type="file"
            accept="image/*"
            capture="environment"
            disabled={remaining === 0}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            className="hidden"
          />
        </label>
      </div>

      {/* Status line */}
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className={converting ? "text-sky-400" : short > 0 ? "text-amber-400" : shortRec > 0 ? "text-sky-400" : photos.length > 0 ? "text-emerald-400" : "text-neutral-500"}>
          {converting
            ? "Convirtiendo HEIC de iPhone… (esto solo pasa la primera vez)"
            : photos.length === 0
            ? (min > 0 ? `Añade mínimo ${min} fotos${recommended > min ? ` (recomendado ${recommended}+)` : ""}` : "Sin fotos añadidas")
            : short > 0
              ? `${photos.length} foto${photos.length === 1 ? "" : "s"} · faltan ${short} para el mínimo`
              : shortRec > 0
                ? `${photos.length} foto${photos.length === 1 ? "" : "s"} ✓ mínimo · añade ${shortRec} más para llegar al recomendado (${recommended})`
                : `${photos.length} foto${photos.length === 1 ? "" : "s"} añadidas ✓`}
        </span>
        <span className="text-neutral-500">Máx {max} · Cada foto se suma, no reemplaza</span>
      </div>
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}

      {/* Thumbnails grid with individual remove buttons */}
      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testid}-thumbs`}>
          {photos.map((f, i) => (
            <div key={`${f.name}-${f.size}-${i}`} className={`relative group ${fullPreview ? "aspect-[3/4]" : "aspect-square"} bg-[#0A0A0A] border border-[#262626] rounded overflow-hidden`}>
              {previews[i] ? (
                <img src={previews[i]} alt="" data-testid={`${testid}-preview-${i}`} className={`w-full h-full ${fullPreview ? "object-contain pb-5" : "object-cover"}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-600"><ImageIcon size={20} /></div>
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testid}-remove-${i}`}
                aria-label={`Quitar foto ${i + 1}`}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1 opacity-100"
              >
                <X size={14} />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-neutral-300 px-1 py-0.5 truncate">
                {f.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MultiPhotoInput;
